import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { advanceReports, type AdvanceReportItem } from '@/api/advanceReports';
import { cashPayments } from '@/api/cashDocs';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import ProjectSelect from '@/components/ProjectSelect';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  advance_payment_id: string;
  purpose: string;
  comment: string;
  project_id: string | null;
  items: AdvanceReportItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  employee_id: '',
  advance_payment_id: '',
  purpose: '',
  comment: '',
  project_id: null,
  items: [],
};

const newRow = (n: number, defaultVatId: string): AdvanceReportItem => ({
  line_number: n,
  description: '',
  expense_account_code: '92',
  amount_without_vat: '0',
  vat_rate_id: defaultVatId,
  vat_amount: '0',
  total_amount: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdvanceReportEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по cash.pko.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('cash.pko.manage');
  const costItemsQ = useQuery({ queryKey: ['cost-items'], queryFn: refs.costItems });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const docQuery = useQuery({
    queryKey: ['advance-report', id],
    queryFn: () => advanceReports.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.advance_reports', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  const employeesQ = useQuery({
    queryKey: ['employees', form.organization_id],
    queryFn: () => refs.employees(form.organization_id),
    enabled: !!form.organization_id,
  });

  // РКО выдачи аванса этому сотруднику (operation_type=TO_PODOTCHET)
  const advancePaymentsQ = useQuery({
    queryKey: ['cash-payments-podotchet', form.organization_id, form.employee_id],
    queryFn: () => cashPayments.list({
      organization_id: form.organization_id,
      employee_id: form.employee_id,
      operation_type: 'TO_PODOTCHET',
      posted: true,
      limit: 100,
    }),
    enabled: !!form.organization_id && !!form.employee_id,
  });

  const defaultVatId = useMemo(() => {
    // Для подотчётов часто без НДС или 20%. Стартовое значение — пусто (без НДС).
    return '';
  }, []);

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1, defaultVatId)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        employee_id: d.employee_id,
        advance_payment_id: d.advance_payment_id ?? '',
        purpose: d.purpose ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          cost_item_id: it.cost_item_id,
          contractor_id: it.contractor_id,
          supplier_doc_number: it.supplier_doc_number,
          supplier_doc_date: it.supplier_doc_date,
          description: it.description,
          expense_account_code: it.expense_account_code,
          amount_without_vat: String(it.amount_without_vat),
          vat_rate_id: it.vat_rate_id,
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs, defaultVatId]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalWithoutVat = form.items.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0);
  const totalVat = form.items.reduce((s, e) => s + Number(e.vat_amount || 0), 0);
  const totalAmount = totalWithoutVat + totalVat;

  // Сумма выданного аванса: из текущего выбранного РКО (если уже выбран)
  // или из сохранённого docQuery.data.advance_received.
  const selectedPayment = advancePaymentsQ.data?.items.find(
    (p) => p.id === form.advance_payment_id,
  );
  const advanceReceived = selectedPayment
    ? Number(selectedPayment.amount)
    : Number(docQuery.data?.advance_received ?? 0);
  const balance = advanceReceived - totalAmount;

  const create = useMutation({
    mutationFn: () => advanceReports.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      employee_id: form.employee_id,
      advance_payment_id: form.advance_payment_id || null,
      purpose: form.purpose || null,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['advance-reports'] });
      navigate(`/advance-reports/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => advanceReports.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      employee_id: form.employee_id,
      advance_payment_id: form.advance_payment_id || null,
      purpose: form.purpose || null,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['advance-report', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await advanceReports.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        employee_id: form.employee_id,
        advance_payment_id: form.advance_payment_id || null,
        purpose: form.purpose || null,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      });
      await advanceReports.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['advance-reports'] });
      navigate(`/advance-reports/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: спершу зберегти правки, потім провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await advanceReports.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        employee_id: form.employee_id,
        advance_payment_id: form.advance_payment_id || null,
        purpose: form.purpose || null,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      });
      await advanceReports.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advance-report', id] });
      qc.invalidateQueries({ queryKey: ['advance-reports'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => advanceReports.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advance-report', id] });
      qc.invalidateQueries({ queryKey: ['advance-reports'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => advanceReports.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advance-reports'] });
      navigate('/advance-reports');
    },
  });

  function setItem(i: number, patch: Partial<AdvanceReportItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        if ('amount_without_vat' in patch || 'vat_rate_id' in patch) {
          const awv = Number(next.amount_without_vat) || 0;
          const vat = vatList.data?.find((v) => v.id === next.vat_rate_id);
          const rate = vat?.rate_percent ?? 0;
          const vatAmt = (awv * rate) / 100;
          next.vat_amount = vatAmt.toFixed(2);
          next.total_amount = (awv + vatAmt).toFixed(2);
        }
        return next;
      }),
    }));
  }
  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1, defaultVatId)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має cash.pko.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати авансовий звіт у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/advance-reports')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий авансовий звіт' : `АО: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/advance-reports')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/advance-reports/${id}/print`)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 2fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>
            № документа
            <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: '0.7rem' }}>
              &nbsp;(авто, якщо порожнє)
            </span>
          </label>
          <input
            value={form.document_number}
            placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Підзвітна особа</label>
          <select value={form.employee_id}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {employeesQ.data?.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.code ? `${emp.code} — ` : ''}{emp.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label>Призначення (мета командировки/витрат)</label>
        <input value={form.purpose}
               onChange={(e) => setForm({ ...form, purpose: e.target.value })}
               disabled={isPosted} />
      </div>

      {!!form.employee_id && (
        <div className="field">
          <label>РКО видачі авансу (необов'язково)</label>
          <select value={form.advance_payment_id}
                  onChange={(e) => setForm({ ...form, advance_payment_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— (без зв'язки)</option>
            {advancePaymentsQ.data?.items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.document_number} від {new Date(p.document_date).toLocaleDateString('uk-UA')}
                {' — '}{Number(p.amount).toLocaleString('uk-UA',
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн
              </option>
            ))}
          </select>
        </div>
      )}

      {!!form.advance_payment_id && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem',
          padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: 6,
          marginBottom: '0.75rem', fontSize: '0.9rem',
        }}>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Видано (за РКО)</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{fmt(advanceReceived)} грн</div>
          </div>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Витрачено</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{fmt(totalAmount)} грн</div>
          </div>
          <div>
            {balance > 0 ? (
              <>
                <div style={{ color: '#b91c1c', fontSize: '0.8rem' }}>Залишок до повернення</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#b91c1c' }}>
                  {fmt(balance)} грн
                </div>
              </>
            ) : balance < 0 ? (
              <>
                <div style={{ color: '#15803d', fontSize: '0.8rem' }}>Перевитрата (борг працівнику)</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#15803d' }}>
                  {fmt(-balance)} грн
                </div>
              </>
            ) : (
              <>
                <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Підсумок</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>0,00 грн</div>
              </>
            )}
          </div>
        </div>
      )}

      <h3 style={{ marginTop: '1.5rem' }}>Витрати</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Опис</th>
            <th style={{ padding: '0.5rem', width: 160 }}>Стаття витрат</th>
            <th style={{ padding: '0.5rem', width: 80 }}>Рах. витрат</th>
            <th style={{ padding: '0.5rem', width: 120 }}>Сума без ПДВ</th>
            <th style={{ padding: '0.5rem', width: 100 }}>ПДВ</th>
            <th style={{ padding: '0.5rem', width: 100 }}>Сума ПДВ</th>
            <th style={{ padding: '0.5rem', width: 120 }}>Усього</th>
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <input value={it.description ?? ''}
                       onChange={(e) => setItem(i, { description: e.target.value })}
                       disabled={isPosted}
                       style={{ width: '100%' }} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <select value={it.cost_item_id ?? ''}
                        onChange={(e) => setItem(i, { cost_item_id: e.target.value || null })}
                        disabled={isPosted}>
                  <option value="">—</option>
                  {costItemsQ.data?.map((c) => (
                    <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input value={it.expense_account_code ?? ''}
                       onChange={(e) => setItem(i, { expense_account_code: e.target.value || null })}
                       disabled={isPosted}
                       style={{ width: '100%' }}
                       placeholder="92" />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.01" value={it.amount_without_vat}
                       onChange={(e) => setItem(i, { amount_without_vat: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right' }} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <select value={it.vat_rate_id ?? ''}
                        onChange={(e) => setItem(i, { vat_rate_id: e.target.value || null })}
                        disabled={isPosted}>
                  <option value="">— (без ПДВ)</option>
                  {vatList.data?.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(Number(it.vat_amount))}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmt(Number(it.total_amount))}</td>
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger" style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={4} style={{ padding: '0.5rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalWithoutVat)}</td>
            <td></td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalVat)}</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalAmount)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      {!isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>
      )}
      <div className="field">
        <label>Проект / напрям</label>
        <ProjectSelect
          value={form.project_id}
          organizationId={form.organization_id}
          onChange={(v) => setForm({ ...form, project_id: v })}
          disabled={isPosted} />
      </div>

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          <strong>Як працює:</strong> на кожен рядок при проведенні створюється проводка
          {' '}<strong>Дт &lt;рах. витрат&gt; — Кт 3721</strong> на суму без ПДВ.
          Якщо вказана ставка ПДВ — додатково{' '}
          <strong>Дт 6442 — Кт 3721</strong> на суму ПДВ (податковий кредит непідтверджений).
          Залишок підзвітної заборгованості видно в ОСВ за рахунком 3721 в розрізі співробітників.
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
